import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { useEntitlements, type BillingInterval } from '@/hooks/useEntitlements';
import { useSites } from '@/hooks/useSites';
import { supabase } from '@/integrations/supabase/client';
import { getStripeEnvironment } from '@/lib/stripe';
import { toast } from '@/hooks/use-toast';
import CheckoutDialog from './CheckoutDialog';

const ModulesSection: React.FC = () => {
  const { modules, getEntitlement, isLoading } = useEntitlements();
  const { sites } = useSites();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [interval, setInterval] = useState<BillingInterval>('month');
  const [checkout, setCheckout] = useState<
    { kind: 'module'; moduleKey: string; interval: BillingInterval } | null
  >(null);
  const facilityCount = Math.max(sites.length, 1);

  const subscribe = (moduleKey: string) => {
    setCheckout({ kind: 'module', moduleKey, interval });
  };

  const callFn = async (
    fn:
      | 'create-module-portal'
      | 'cancel-module-subscription'
      | 'resume-module-subscription',
    body: Record<string, unknown>,
    key: string,
  ) => {
    setBusyKey(key);
    try {
      const env = (() => { try { return getStripeEnvironment(); } catch { return 'sandbox' as const; } })();
      const { data, error } = await supabase.functions.invoke(fn, {
        body: { ...body, environment: env },
      });
      if (error) throw error;
      if (fn === 'create-module-portal') {
        const payload = data as any;
        if (payload?.info === 'NO_BILLING_CUSTOMER') {
          toast({ title: 'No billing yet', description: payload.message });
          return;
        }
        const url = payload?.url;
        if (!url) throw new Error('No URL returned');
        window.open(url, '_blank');
      } else {
        toast({ title: 'Updated', description: 'Subscription updated.' });
      }
    } catch (err: any) {
      toast({
        title: 'Action failed',
        description: err?.message || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setBusyKey(null);
    }
  };

  if (isLoading) {
    return (
      <Card className="bg-card rounded-xl p-6 border">
        <p className="text-sm text-muted-foreground">Loading add-on modules…</p>
      </Card>
    );
  }

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-foreground">Add-on modules</h2>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-md border border-border overflow-hidden text-xs">
            <button
              className={`px-3 py-1.5 ${interval === 'month' ? 'bg-foreground text-background' : 'bg-card text-muted-foreground'}`}
              onClick={() => setInterval('month')}
            >
              Monthly
            </button>
            <button
              className={`px-3 py-1.5 ${interval === 'year' ? 'bg-foreground text-background' : 'bg-card text-muted-foreground'}`}
              onClick={() => setInterval('year')}
            >
              Annual <span className="ml-1 text-emerald-600">first year −1 mo</span>
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            {facilityCount} {facilityCount === 1 ? 'facility' : 'facilities'} active
          </p>
        </div>
      </div>
      <Card className="bg-card rounded-xl border divide-y divide-border">
        {modules.map((m) => {
          const ent = getEntitlement(m.key);
          const isSubscribed = ent && ['active', 'trialing', 'past_due'].includes(ent.status);
          const isCanceling = !!ent?.cancel_at_period_end;
          const monthly = m.price_per_facility_cents / 100;
          const yearly = monthly * 11;
          const price = interval === 'year' ? yearly : monthly;
          const periodLabel = interval === 'year' ? '/ facility / year' : '/ facility / mo';
          const busy = busyKey === m.key;
          return (
            <div key={m.key} className="flex items-start justify-between gap-4 p-4">
              <div className="space-y-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-foreground">{m.name}</p>
                  {isSubscribed ? (
                    <Badge
                      variant="outline"
                      className={
                        ent?.status === 'past_due'
                          ? 'text-[10px] border-red-500/60 text-red-700'
                          : 'text-[10px] border-emerald-500/60 text-emerald-700'
                      }
                    >
                      {ent?.status === 'trialing'
                        ? 'Trialing'
                        : ent?.status === 'past_due'
                          ? 'Past due'
                          : 'Active'}
                    </Badge>
                  ) : null}
                  {isCanceling && ent?.current_period_end && (
                    <Badge variant="outline" className="text-[10px]">
                      Cancels {new Date(ent.current_period_end).toLocaleDateString()}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{m.description}</p>
                <p className="text-xs text-muted-foreground tabular-nums">
                  ${price.toLocaleString()} {periodLabel} · est. $
                  {(price * facilityCount).toLocaleString()} {interval === 'year' ? '/ year' : '/ mo'} at current facility count
                </p>
              </div>
              <div className="shrink-0 flex items-center gap-2">
                {isSubscribed ? (
                  <>
                    {isCanceling ? (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        onClick={() =>
                          callFn('resume-module-subscription', { kind: 'module', moduleKey: m.key }, m.key)
                        }
                      >
                        {busy && <Loader2 className="h-3 w-3 animate-spin mr-2" />}
                        Resume
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() =>
                          callFn('cancel-module-subscription', { kind: 'module', moduleKey: m.key }, m.key)
                        }
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() => callFn('create-module-portal', {}, m.key)}
                    >
                      Manage
                    </Button>
                  </>
                ) : (
                  <Button size="sm" disabled={busy} onClick={() => subscribe(m.key)}>
                    Subscribe
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </Card>
      <CheckoutDialog
        open={!!checkout}
        onOpenChange={(v) => { if (!v) setCheckout(null); }}
        request={checkout}
        title="Subscribe to module"
        description={checkout?.interval === 'year'
          ? 'Charged per active facility. The first annual term includes one month free; renewal is at the full annual rate.'
          : 'Charged per active facility. You can cancel any time.'}
      />
    </section>
  );
};

export default ModulesSection;
